import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { useState, useEffect } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { User, Mail, IdCard, Phone, MapPin, Save, Edit, X, Check, Camera } from "lucide-react";
import Swal from "sweetalert2";
import { obtenerTodos, modificarDatosPersonales, modificarFoto } from "../../../../api/usuarioServices";
import { obtenerTiposDocumento, TipoDocumento } from "../../../../api/tipoDocumentoService";

interface UserProfile {
  email: string;
  torre: string;
  apartamento: string;
  nombre: string;
  tipoDocumento: string;
  numeroDocumento: string;
  numeroCelular: string;
  fotoPerfil?: string;
}

export default function UserProfilePage() {
  const { session } = useAuth();
  const [profileData, setProfileData] = useState<UserProfile>({
    email: "",
    torre: "",
    apartamento: "",
    nombre: "",
    tipoDocumento: "",
    numeroDocumento: "",
    numeroCelular: "",
    fotoPerfil: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState<UserProfile | null>(null);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [isSavingPhoto, setIsSavingPhoto] = useState(false);

  // Cargar tipos de documento al montar el componente
  useEffect(() => {
    const loadTiposDocumento = async () => {
      try {
        const response = await obtenerTiposDocumento();
        if (response.code === "1" && response.object_) {
          setTiposDocumento(response.object_);
        }
      } catch (error) {
        console.error("Error al cargar tipos de documento:", error);
      }
    };

    loadTiposDocumento();
  }, []);

  // Cargar datos del perfil al montar el componente
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);

        // Obtener datos del contexto de sesión
        const email = session?.usuario?.email || "";
        const codigoApartamento = session?.apartamento?.id || "";
        const tipoUsuario = session?.rol?.id|| "";

        // Llamar al servicio ObtenerTodos
        const response = await obtenerTodos({
          email: email,
          "Codigo apartamento": codigoApartamento,
          "TipoUsuario": tipoUsuario
        });

        if (response.code === "1" && response.object_ && response.object_.length > 0) {
          const userData = response.object_[0];
          const profile: UserProfile = {
            email: userData.usu_email,
            torre: userData.tor_nombre,
            apartamento: userData.apt_nombre,
            nombre: userData.usu_nombre_completo,
            tipoDocumento: userData.usu_tipo_documento,
            numeroDocumento: userData.usu_numero_documento,
            numeroCelular: userData.usu_numero_celular,
            fotoPerfil: userData.usu_ruta_foto || ""
          };

          setProfileData(profile);
          setOriginalData(profile);
          setPreviewImage(profile.fotoPerfil || "");
        } else {
          throw new Error(response.message || "No se encontraron datos del usuario");
        }
      } catch (error) {
        console.error("Error al cargar perfil:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar los datos del perfil",
          confirmButtonColor: "#d33",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (session?.usuario?.email) {
      loadProfile();
    }
  }, [session]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setOriginalData({ ...profileData });
  };

  const handleCancel = () => {
    if (originalData) {
      setProfileData(originalData);
      setPreviewImage(originalData.fotoPerfil || "");
    }
    setIsEditing(false);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tamaño (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          text: "La imagen no debe superar los 5MB",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      // Validar tipo
      if (!file.type.startsWith("image/")) {
        Swal.fire({
          icon: "warning",
          title: "Formato inválido",
          text: "Por favor seleccione una imagen válida",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;

        try {
          setIsSavingPhoto(true);

          const fotoPayload = {
            email: session?.usuario?.email || session?.email || profileData.email,
            "apartamento codigo": session?.apartamento?.id || "",
            "Tipo usuario codigo": session?.rol?.id || "",
            foto_base64: base64String
          };

          const response = await modificarFoto(fotoPayload);

          if (response.code !== "1" && response.code !== 1) {
            throw new Error(response.message || "Error al actualizar la foto");
          }

          setPreviewImage(base64String);
          setProfileData(prev => ({ ...prev, fotoPerfil: base64String }));
          setOriginalData(prev => prev ? { ...prev, fotoPerfil: base64String } : null);

          Swal.fire({
            icon: "success",
            title: response.message || "¡Foto actualizada!",
            confirmButtonColor: "#3085d6",
            timer: 2000,
            timerProgressBar: true,
          });

        } catch (error) {
          console.error("Error al actualizar foto:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error instanceof Error ? error.message : "Error al actualizar la foto",
            confirmButtonColor: "#d33",
          });
        } finally {
          setIsSavingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      // Validaciones básicas
      if (!profileData.nombre.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "El nombre es obligatorio",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      if (!profileData.email.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "El email es obligatorio",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email)) {
        Swal.fire({
          icon: "warning",
          title: "Email inválido",
          text: "Por favor, ingrese un email válido",
          confirmButtonColor: "#3085d6",
        });
        return;
      }

      setIsSaving(true);

      // Preparar payload con datos del contexto y del formulario
      const payload = {
        email: session?.usuario?.email || session?.email || profileData.email,
        "apartamento codigo": session?.apartamento?.id || "",
        "Tipo usuario codigo": session?.rol?.id || "",
        "nombre completo": profileData.nombre,
        "tipo documento codigo": profileData.tipoDocumento,
        "numero documento": profileData.numeroDocumento,
        "numero celular": profileData.numeroCelular
      };

      // Debug: Mostrar payload en consola
      console.log("=== DEBUG MODIFICAR DATOS PERSONALES ===");
      console.log("Session completa:", session);
      console.log("Payload enviado:", payload);

      // Validar que todos los campos requeridos tengan valor
      if (!payload.email || !payload["apartamento codigo"] || !payload["Tipo usuario codigo"]) {
        Swal.fire({
          icon: "error",
          title: "Datos de sesión incompletos",
          text: `Faltan datos: email=${payload.email}, apt=${payload["apartamento codigo"]}, tipo=${payload["Tipo usuario codigo"]}`,
          confirmButtonColor: "#d33",
        });
        setIsSaving(false);
        return;
      }

      // Modificar solo datos personales
      const response = await modificarDatosPersonales(payload);

      console.log("=== RESPUESTA MODIFICAR DATOS ===");
      console.log("Response:", response);

      if (response.code !== "1" && response.code !== 1) {
        throw new Error(response.message || "Error al actualizar los datos");
      }

      Swal.fire({
        icon: "success",
        title: "¡Perfil actualizado!",
        text: "Los datos han sido guardados exitosamente",
        confirmButtonColor: "#3085d6",
        timer: 2000,
        timerProgressBar: true,
      });

      setIsEditing(false);
      setOriginalData({ ...profileData });

    } catch (error) {
      console.error("Error al actualizar perfil:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "Error al actualizar el perfil",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-blue-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando perfil...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-military-600">Perfil de Usuario</h2>
          {!isEditing && (
            <Button
              onClick={handleEdit}
              className="bg-military-500 hover:bg-military-600 text-white rounded-lg transition flex items-center gap-2 w-full sm:w-auto"
            >
              <Edit size={20} />
              Editar
            </Button>
          )}
        </div>

        {/* Layout responsivo */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Formulario principal - 2/3 del ancho en desktop */}
          <div className="xl:col-span-2 space-y-6">
            {/* Card de datos personales */}
            <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl text-military-600 flex items-center gap-2">
                      <User size={24} />
                      Datos Personales
                    </CardTitle>
                    <p className="text-gray-600 text-sm mt-2">
                      {isEditing
                        ? "Modifica tus datos personales y guarda los cambios"
                        : "Información personal registrada en el sistema"
                      }
                    </p>
                  </div>

                  {/* Foto de perfil en la esquina superior derecha */}
                  <div
                    className="relative group ml-0 sm:ml-4 mt-4 sm:mt-0"
                    onMouseEnter={() => setIsHoveringImage(true)}
                    onMouseLeave={() => setIsHoveringImage(false)}
                  >
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                      {previewImage ? (
                        <img
                          src={previewImage}
                          alt="Foto de perfil"
                          className="w-full h-full rounded-full object-cover border-4 border-military-500 shadow-lg"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-military-400 to-military-600 flex items-center justify-center border-4 border-military-500 shadow-lg">
                          <User size={40} className="text-white" />
                        </div>
                      )}

                      {/* Overlay al hacer hover - siempre visible */}
                      <div className={`absolute inset-0 rounded-full bg-black/50 flex items-center justify-center transition-opacity ${
                        isHoveringImage ? 'opacity-100' : 'opacity-0'
                      }`}>
                        {isSavingPhoto ? (
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <Camera size={24} className="text-white" />
                        )}
                      </div>
                    </div>

                    {/* Input oculto para seleccionar imagen - siempre activo */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={isSavingPhoto}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full disabled:cursor-not-allowed"
                      title="Cambiar foto de perfil"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* Email - NO EDITABLE */}
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail size={16} className="inline mr-1" />
                      Correo Electrónico *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profileData.email}
                      disabled={true}
                      className="w-full rounded-lg bg-gray-50 border-gray-200 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Este campo no se puede editar</p>
                  </div>

                  {/* Torre - NO EDITABLE */}
                  <div>
                    <label htmlFor="torre" className="block text-sm font-medium text-gray-700 mb-1">
                      <MapPin size={16} className="inline mr-1" />
                      Torre
                    </label>
                    <Input
                      id="torre"
                      name="torre"
                      value={profileData.torre}
                      disabled={true}
                      className="w-full rounded-lg bg-gray-50 border-gray-200 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Campo fijo</p>
                  </div>

                  {/* Apartamento - NO EDITABLE */}
                  <div>
                    <label htmlFor="apartamento" className="block text-sm font-medium text-gray-700 mb-1">
                      <MapPin size={16} className="inline mr-1" />
                      Apartamento
                    </label>
                    <Input
                      id="apartamento"
                      name="apartamento"
                      value={profileData.apartamento}
                      disabled={true}
                      className="w-full rounded-lg bg-gray-50 border-gray-200 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Campo fijo</p>
                  </div>

                  {/* Nombre */}
                  <div className="md:col-span-2">
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                      <User size={16} className="inline mr-1" />
                      Nombre Completo *
                    </label>
                    <Input
                      id="nombre"
                      name="nombre"
                      value={profileData.nombre}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full rounded-lg ${
                        isEditing 
                          ? "bg-white border-gray-300 focus:border-military-500 focus:ring-military-500" 
                          : "bg-gray-50 border-gray-200"
                      }`}
                    />
                  </div>

                  {/* Tipo de documento */}
                  <div>
                    <label htmlFor="tipoDocumento" className="block text-sm font-medium text-gray-700 mb-1">
                      <IdCard size={16} className="inline mr-1" />
                      Tipo de Documento
                    </label>
                    {isEditing ? (
                      <select
                        id="tipoDocumento"
                        name="tipoDocumento"
                        value={profileData.tipoDocumento}
                        onChange={handleInputChange}
                        className="w-full rounded-lg bg-white border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                      >
                        <option value="">Seleccionar...</option>
                        {tiposDocumento
                          .filter(tipo => tipo.tdo_estatus === "A")
                          .map(tipo => (
                            <option key={tipo.tdo_code} value={tipo.tdo_code}>
                              {tipo.tdo_nombre}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <Input
                        value={tiposDocumento.find(t => t.tdo_code === profileData.tipoDocumento)?.tdo_nombre || profileData.tipoDocumento}
                        disabled
                        className="w-full rounded-lg bg-gray-50 border-gray-200"
                      />
                    )}
                  </div>

                  {/* Número de documento */}
                  <div>
                    <label htmlFor="numeroDocumento" className="block text-sm font-medium text-gray-700 mb-1">
                      <IdCard size={16} className="inline mr-1" />
                      Número de Documento
                    </label>
                    <Input
                      id="numeroDocumento"
                      name="numeroDocumento"
                      value={profileData.numeroDocumento}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className={`w-full rounded-lg ${
                        isEditing 
                          ? "bg-white border-gray-300 focus:border-military-500 focus:ring-military-500" 
                          : "bg-gray-50 border-gray-200"
                      }`}
                    />
                  </div>

                  {/* Número celular */}
                  <div className="md:col-span-2">
                    <label htmlFor="numeroCelular" className="block text-sm font-medium text-gray-700 mb-1">
                      <Phone size={16} className="inline mr-1" />
                      Número Celular
                    </label>
                    <Input
                      id="numeroCelular"
                      name="numeroCelular"
                      value={profileData.numeroCelular}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Ej: 300 200 34 56"
                      className={`w-full rounded-lg ${
                        isEditing 
                          ? "bg-white border-gray-300 focus:border-military-500 focus:ring-military-500" 
                          : "bg-gray-50 border-gray-200"
                      }`}
                    />
                  </div>
                </div>

                {/* Botones de acción */}
                {isEditing && (
                  <div className="flex flex-col sm:flex-row gap-4 mt-6 pt-6 border-t">
                    <Button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-lg transition flex items-center justify-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Check size={20} />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      disabled={isSaving}
                      variant="outline"
                      className="flex-1 border-gray-300 hover:bg-gray-50 rounded-lg transition flex items-center justify-center gap-2"
                    >
                      <X size={20} />
                      Cancelar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Panel de información adicional - 1/3 del ancho en desktop */}
          <div className="xl:col-span-1">
            <div className="space-y-6">
              {/* Notas importantes */}
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <IdCard size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-amber-900 mb-3">Notas Importantes</h4>
                      <ul className="text-sm text-amber-800 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Los campos marcados con * son obligatorios</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Email, Torre y Apartamento no se pueden editar</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Los cambios requieren verificación del administrador</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>Mantenga sus datos actualizados para mejor comunicación</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>La foto de perfil debe ser menor a 5MB</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}